// src/components/AddToHomeScreenModal.js
import React, { useState, useEffect } from 'react';

const AddToHomeScreenModal = ({ onDismiss }) => {
  const [showModal, setShowModal] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);
  const [installing, setInstalling] = useState(false);

  useEffect(() => {
    const iOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    const android = /Android/.test(navigator.userAgent);
    setIsIOS(iOS);
    setIsAndroid(android);

    // Already running as installed PWA — never show
    const isStandalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    if (isStandalone) return;

    // Show every time opened in browser, unless permanently dismissed
    const dismissed = localStorage.getItem('aths_dismissed_v3');
    if (!dismissed) {
      setTimeout(() => setShowModal(true), 800);
    }

    const handleBeforeInstallPrompt = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    const handleAppInstalled = () => {
      setShowModal(false);
      localStorage.setItem('aths_dismissed_v3', 'true');
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('appinstalled', handleAppInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('appinstalled', handleAppInstalled);
    };
  }, []);

  const handleInstall = async () => {
    if (!deferredPrompt) return;
    setInstalling(true);
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    setInstalling(false);
    if (outcome === 'accepted') handlePermanentDismiss();
    setDeferredPrompt(null);
  };

  const handlePermanentDismiss = () => {
    setShowModal(false);
    localStorage.setItem('aths_dismissed_v3', 'true');
    if (onDismiss) onDismiss();
  };

  const handleRemindLater = () => {
    setShowModal(false);
    if (onDismiss) onDismiss();
  };

  if (!showModal) return null;

  const Step = ({ num, children }) => (
    <div className="flex items-start gap-3">
      <div className="w-6 h-6 rounded-full bg-orange-500 text-white text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">
        {num}
      </div>
      <div className="flex-1 text-sm text-gray-700 leading-snug pt-0.5">{children}</div>
    </div>
  );

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-end justify-center"
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={(e) => e.target === e.currentTarget && handleRemindLater()}
    >
      <style>{`
        @keyframes aths-up {
          from { transform: translateY(100%); }
          to   { transform: translateY(0); }
        }
      `}</style>

      <div
        className="bg-white w-full max-w-lg rounded-t-2xl shadow-2xl overflow-hidden"
        style={{ animation: 'aths-up 0.3s cubic-bezier(0.22,1,0.36,1)' }}
      >
        {/* Drag handle */}
        <div className="flex justify-center pt-3 pb-2">
          <div className="w-9 h-1 bg-gray-200 rounded-full" />
        </div>

        {/* Header — matches webapp orange header style */}
        <div className="bg-gradient-to-r from-orange-500 to-orange-400 px-4 py-4 flex items-center gap-3">
         
          <div className="flex-1">
            <p className="text-white font-bold text-base leading-tight mb-0">Add to Home Screen</p>
            <p className="text-orange-100 text-xs mt-0.5 mb-1">You're in a browser — install for the full experience</p>
          </div>
        </div>

        {/* Body */}
        <div className="px-4 pt-4 pb-2 max-h-[55vh] overflow-y-auto">

          {/* ── iOS ── */}
          {isIOS && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                How to install on iPhone / iPad
              </p>
              <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-4">
                <Step num="1">
                  Open this page in <span className="font-semibold text-gray-900">Safari</span>
                  <span className="text-gray-500"> (required — won't work in Chrome or other browsers)</span>
                </Step>
                <div className="border-t border-gray-100" />
                <Step num="2">
                  Tap the{' '}
                  <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-500 rounded-md align-middle mx-0.5">
                    <svg className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth={2.5} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                      <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13"/>
                    </svg>
                  </span>{' '}
                  <span className="font-semibold text-gray-900">Share button</span>
                  <span className="text-gray-500"> in Safari's bottom toolbar</span>
                </Step>
                <div className="border-t border-gray-100" />
                <Step num="3">
                  Scroll down and tap{' '}
                  <span className="font-semibold text-gray-900">"Add to Home Screen"</span>
                </Step>
                <div className="border-t border-gray-100" />
                <Step num="4">
                  Tap <span className="font-semibold text-gray-900">"Add"</span>
                  <span className="text-gray-500"> — the app icon will appear on your home screen</span>
                </Step>
              </div>
            </div>
          )}

          {/* ── Android without native prompt ── */}
          {isAndroid && !deferredPrompt && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                How to install on Android
              </p>
              <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-4">
                <Step num="1">
                  Tap the{' '}
                  <span className="font-semibold text-gray-900">⋮ menu</span>
                  <span className="text-gray-500"> (three dots) in the top-right corner of Chrome</span>
                </Step>
                <div className="border-t border-gray-100" />
                <Step num="2">
                  Tap{' '}
                  <span className="font-semibold text-gray-900">"Add to Home screen"</span>
                  <span className="text-gray-500"> — or "Install app" if you see that instead</span>
                </Step>
                <div className="border-t border-gray-100" />
                <Step num="3">
                  Tap <span className="font-semibold text-gray-900">"Add"</span>
                  <span className="text-gray-500"> to confirm</span>
                </Step>
              </div>
            </div>
          )}

          {/* ── Chrome/Edge with native prompt ── */}
          {deferredPrompt && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                Install with one tap
              </p>
              <div className="bg-white border border-gray-100 rounded-xl p-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
                  <svg className="w-5 h-5 text-orange-600" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                    <path d="M12 2v13M5 9l7 7 7-7"/><path d="M3 19h18"/>
                  </svg>
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">Ready to install</p>
                  <p className="text-xs text-gray-500 mt-0.5">Tap the button below — Chrome will install the app instantly</p>
                </div>
              </div>
            </div>
          )}

          {/* ── Desktop / unknown ── */}
          {!isIOS && !isAndroid && !deferredPrompt && (
            <div>
              <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-3">
                How to install
              </p>
              <div className="bg-white border border-gray-100 rounded-xl p-4 space-y-4">
                <Step num="1">
                  In your browser's address bar or menu, look for{' '}
                  <span className="font-semibold text-gray-900">"Install"</span>
                  {' '}or{' '}
                  <span className="font-semibold text-gray-900">"Add to Home Screen"</span>
                </Step>
                <div className="border-t border-gray-100" />
                <Step num="2">
                  Follow the prompts to install the app to your device
                </Step>
              </div>
            </div>
          )}

          {/* Why install — matches the green/orange badge style in Profile */}
          <div className="mt-3 px-1 pb-1 flex flex-wrap gap-2">
            {[
              { label: 'Faster loading', icon: 'M13 2L3 14h9l-1 8 10-12h-9l1-8z' },
              { label: 'Full screen', icon: 'M8 3H5a2 2 0 00-2 2v3m18 0V5a2 2 0 00-2-2h-3m0 18h3a2 2 0 002-2v-3M3 16v3a2 2 0 002 2h3' },
              { label: 'Notifications', icon: 'M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6 6 0 10-12 0v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9' },
            ].map(({ label, icon }) => (
              <div key={label} className="flex items-center gap-1.5 px-2.5 py-1 bg-orange-50 rounded-lg">
                <svg className="w-3.5 h-3.5 text-orange-500 flex-shrink-0" fill="none" stroke="currentColor" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" viewBox="0 0 24 24">
                  <path d={icon}/>
                </svg>
                <span className="text-xs text-orange-700 font-medium">{label}</span>
              </div>
            ))}
          </div>

        </div>

        {/* Buttons — match webapp button style exactly */}
        <div className="px-4 pt-3 pb-7 space-y-2">
          {deferredPrompt && (
            <button
              onClick={handleInstall}
              disabled={installing}
              className="w-full py-3 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white rounded-xl font-semibold text-sm transition-all disabled:opacity-60"
            >
              {installing ? 'Installing…' : 'Install App Now'}
            </button>
          )}
          <div className="flex gap-2">
            <button
              onClick={handlePermanentDismiss}
              className="flex-1 py-3 bg-orange-500 hover:bg-orange-600 active:scale-95 text-white rounded-xl font-semibold text-sm transition-all"
            >
              Already Added ✓
            </button>
            <button
              onClick={handleRemindLater}
              className="flex-1 py-3 bg-gray-100 hover:bg-gray-200 active:scale-95 text-gray-600 rounded-xl font-semibold text-sm transition-all"
            >
              Remind Me Later
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default AddToHomeScreenModal;